import { useRef, useState } from 'react';
import { t } from '../../lib/i18n';

/**
 * Adds the time since the last update to the watched total, but only for normal playback (a forward step of at most
 * `maxStep` seconds). Jumps from seeking are not counted.
 */
export function accumulateWatched(watched: number, previousTime: number, time: number, maxStep = 2): number {
  const step = time - previousTime;
  return step > 0 && step <= maxStep ? watched + step : watched;
}

/** Has the viewer met the rules? Without a minimum, the video must reach its end. */
export function watchComplete(o: { watched: number; ended: boolean; mustWatchToEnd?: boolean; minWatchSeconds?: number }): boolean {
  const min = o.minWatchSeconds ?? 0;
  if (o.mustWatchToEnd) return o.ended && o.watched >= min;
  if (min > 0) return o.watched >= min;
  return o.ended;
}

export interface VideoPlayerProps {
  src: string;
  /** Accessible name for the player. */
  title: string;
  poster?: string;
  /** Must reach the end (and cannot skip beyond what was already played). */
  mustWatchToEnd?: boolean;
  /** Seconds that must have played before completion (0 = none). */
  minWatchSeconds?: number;
  /** Called whenever the watched total grows by a whole second. */
  onWatched?: (seconds: number) => void;
  /** Called once when the viewer has met the rules. */
  onComplete?: (watchedSeconds: number) => void;
}

/** Native video player with an optional must-watch-to-end rule and a minimum watch time. */
export function VideoPlayer({ src, title, poster, mustWatchToEnd, minWatchSeconds = 0, onWatched, onComplete }: VideoPlayerProps) {
  const video = useRef<HTMLVideoElement>(null);
  const last = useRef(0);
  const furthest = useRef(0);
  const watchedRef = useRef(0);
  const ended = useRef(false);
  const completed = useRef(false);
  const [watched, setWatched] = useState(0);
  const [complete, setComplete] = useState(false);

  const check = () => {
    if (completed.current) return;
    if (watchComplete({ watched: watchedRef.current, ended: ended.current, mustWatchToEnd, minWatchSeconds })) {
      completed.current = true;
      setComplete(true);
      onComplete?.(Math.floor(watchedRef.current));
    }
  };

  const onTime = () => {
    const el = video.current;
    if (!el) return;
    const before = Math.floor(watchedRef.current);
    watchedRef.current = accumulateWatched(watchedRef.current, last.current, el.currentTime);
    if (el.currentTime - last.current <= 2 && el.currentTime > furthest.current) furthest.current = el.currentTime;
    last.current = el.currentTime;
    const now = Math.floor(watchedRef.current);
    if (now !== before) {
      setWatched(now);
      onWatched?.(now);
    }
    check();
  };

  const onSeeking = () => {
    const el = video.current;
    if (!el || completed.current || !mustWatchToEnd) return;
    if (el.currentTime > furthest.current + 0.5) el.currentTime = furthest.current;
    last.current = el.currentTime;
  };

  const remaining = Math.max(0, minWatchSeconds - watched);
  const message = complete
    ? t('p3w.video.complete')
    : mustWatchToEnd
      ? t('p3w.video.mustWatch')
      : remaining > 0
        ? t('p3w.video.remaining', { seconds: remaining })
        : '';
  return (
    <div className="space-y-2">
      <video
        ref={video}
        src={src}
        poster={poster}
        controls
        preload="metadata"
        aria-label={title}
        className="w-full rounded-lg bg-black"
        onTimeUpdate={onTime}
        onSeeking={onSeeking}
        onEnded={() => {
          ended.current = true;
          onTime();
          check();
        }}
      />
      <p role="status" className="text-sm text-slate-700">
        {message}
      </p>
    </div>
  );
}
